import { useEffect, useState } from 'react'
import { Combobox } from '@cinesend/ui-kit'
import useStore from '../../../hooks/use_store'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { MISSING_UNIQUE_NAME } from '../../../constants/category_display'

const validTypes = ['warning-slates', 'ad-pools']

const CategoryField = ({ title, options, value, assetID, cycleID, langID, type, disabled, isMulti: isMultiProp }) => {
  const [currentValues, setCurrentValues] = useState([])
  const [category, setCategory] = useState()
  const { createModel } = useStore()
  const queryClient = useQueryClient()
  const [errorMessage, setErrorMessage] = useState(null)

  useEffect(() => {
    setCurrentValues(value)
  }, [value])

  const updateAssetCategory = (selected) => {
    // if its not an array then we're dealing with a single item Combobox
    if (!Array.isArray(selected)) {
      if (selected === null) {
        setCategory(currentValues[0])
        removeCategory.mutate()
        return
      }
      addCategory(findOption(selected))
      return
    }

    const newValues = selected.map(findOption)

    if (currentValues.length < newValues.length) {
      // get difference and add
      const category = newValues.filter((item) => !currentValues.includes(item))[0]
      addCategory(category)
    } else {
      // get difference and remove
      const category = currentValues.filter((item) => !newValues.includes(item))[0]
      setCategory(category)
      removeCategory.mutate()
    }
  }

  const addCategory = (category) => {
    createModel.mutate(
      {
        endpoint: `categories/${category.value}/add-items`,
        data: {
          type: validTypes.includes(type) ? type : 'assets',
          ids: [assetID],
          cycleID: cycleID,
        },
      },
      {
        onSuccess: () => {
          setErrorMessage(null)
          setCurrentValues([...currentValues, category])
          queryClient.invalidateQueries([`cycles/${cycleID}/assets/${assetID}`])
        },
        onError: (err) => {
          console.error(err)
          setErrorMessage(err?.response?.data?.error ?? 'unknown error')
        },
      }
    )
  }

  const removeCategory = useMutation(
    () =>
      axios.delete(`/categories/${category.value}/remove-items`, {
        data: { ids: [assetID], cycleID: cycleID, type: validTypes.includes(type) ? type : 'assets' },
      }),
    {
      onSuccess: () => {
        setCurrentValues(currentValues.filter((item) => item !== category))
        queryClient.invalidateQueries([`cycles/${cycleID}/assets/${assetID}`])
      },
      onError: (err) => console.error(err),
    }
  )

  const getTitle = (category) => {
    if (['warning-slates', 'featured-content'].includes(type)) {
      return (
        category.category_values?.find((v) => v.language_id === langID)?.value ||
        category.category_values?.[0]?.value ||
        ''
      )
    }
    return category.external_unique_name || MISSING_UNIQUE_NAME
  }

  const optionList = options?.map((item) => ({ value: item.id, label: getTitle(item) })) ?? []
  const findOption = (id) =>
    currentValues.find((opt) => String(opt.value) === id) ?? optionList.find((opt) => String(opt.value) === id)

  const isMulti =
    isMultiProp !== undefined ? isMultiProp : !['warning-slates', 'featured-content', 'ad-pools'].includes(type)

  return (
    <Combobox
      label={title}
      options={optionList.map((option) => ({ ...option, value: String(option.value) }))}
      value={
        isMulti
          ? currentValues.map((opt) => String(opt.value))
          : currentValues[0]
            ? String(currentValues[0].value)
            : null
      }
      multiple={isMulti}
      clearable={!isMultiProp && (type === 'warning-slates' || type === 'ad-pools')}
      onValueChange={updateAssetCategory}
      className={'pt-2'}
      error={errorMessage ?? undefined}
      disabled={disabled}
    />
  )
}

export default CategoryField
